
"use client";

import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef, type FC, type ReactNode } from 'react';
import {
  User as FirebaseUser,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  onIdTokenChanged
} from 'firebase/auth';
import { auth, db as importedDbInstance } from '@/lib/firebase';
import { doc, getDoc, setDoc, Timestamp, serverTimestamp, type FieldValue, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { app as firebaseApp } from '@/lib/firebase';
import { useRouter, useSearchParams } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { PRICING_TIERS, TIER_DETAILS, type UserTier, type CurrencyCode, type PriceDetail, ADDON_PRICES, REFEREE_SIGNUP_BONUS, DEFAULT_RAZORPAY_INTEGRATION_ENABLED } from '@/lib/constants';
import { v4 as uuidv4 } from 'uuid';
import { IntegrationInProgressModal } from '@/components/modals/IntegrationInProgressModal';
import { useRemoteConfig } from './RemoteConfigContext';

interface User extends FirebaseUser {}

export interface UserProfileData {
  email: string | null;
  displayName: string | null;
  createdAt: Timestamp | FieldValue;
  lastLoginAt: Timestamp | FieldValue;
  currentTier: UserTier;
  tierValidUntil: Timestamp | null;
  monthlyRepliesUsed: number;
  monthlyImageUploadsUsed: number;
  monthlyHintsHelpsUsed: number;
  lastMonthlyReset: Timestamp | null;
  currentSessionRepliesUsed?: number;
  currentSessionImageUploadsUsed?: number;
  currentSessionHintsHelpsUsed?: number;
  isPremium: boolean; 
  premiumValidUntil: Timestamp | null; 
  premiumSource: 'coupon' | 'razorpay' | 'razorpay_addon' | null;
  freeQuestionsUsed: number;
  referralCode: string;
  referredBy: string | null;
  userCountry?: CurrencyCode;
  isAdmin?: boolean;
  lastAddonPurchaseAt?: Timestamp;
  lastFullFeedbackAddonPurchaseAt?: Timestamp | null; 
  dayPassActiveUntil?: Timestamp | null;
  lastDayPassPurchaseAt?: Timestamp | null;
}

interface OptimisticUserData {
  displayName: string | null;
  photoURL: string | null;
  email: string | null;
}

interface RazorpayPaymentResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface AuthContextType {
  user: User | null;
  optimisticUser: OptimisticUserData | null;
  loading: boolean;
  currentTier: UserTier;
  tierValidUntil: Timestamp | null;
  isEffectivelyPremium: boolean; 
  monthlyRepliesUsed: number;
  monthlyImageUploadsUsed: number;
  monthlyHintsHelpsUsed: number;
  getQuotaFor: (metric: 'replies' | 'images' | 'hintsHelps' | 'feedback') => number | 'unlimited';
  isPremium: boolean; 
  premiumValidUntil: Timestamp | null; 
  premiumSource: 'coupon' | 'razorpay' | 'razorpay_addon' | null;
  isAdmin: boolean;
  freeQuestionsUsed: number;
  referralCode: string | null;
  userCountry: CurrencyCode;
  currencySymbol: string;
  lastFullFeedbackAddonPurchaseAt: Timestamp | null; 
  dayPassActiveUntil: Timestamp | null;
  lastDayPassPurchaseAt: Timestamp | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
  incrementUsage: (metric: 'replies' | 'images' | 'hintsHelps' | 'feedback', amount?: number) => Promise<void>;
  triggerUpsell: (action: 'subscribeToTier' | 'purchaseAddon' | 'purchasePayPerUseSession', details?: Record<string, any>) => void;
  fetchAndSetDisplayCountry: () => Promise<void>;
  updateUserPreferredCurrency: (newCurrency: CurrencyCode) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const OPTIMISTIC_USER_STORAGE_KEY = 'optimisticUserData';
const DEFAULT_CURRENCY_CODE: CurrencyCode = 'USD';
const AUTH_CONTEXT_DEBUG_PREFIX = "[AuthContext]";

declare global {
  interface Window {
    Razorpay: any;
    google?: any;
  }
}

function getCurrencyFromCountryCode(countryCode: string | null): CurrencyCode {
  if (!countryCode) return DEFAULT_CURRENCY_CODE;
  const lowerCode = countryCode.toLowerCase();
  if (lowerCode === 'in') return 'INR';
  if (lowerCode === 'gb' || lowerCode === 'uk') return 'GBP';
  if (['de', 'fr', 'es', 'it', 'nl', 'be', 'at', 'pt', 'fi', 'ie'].includes(lowerCode)) return 'EUR';
  if (lowerCode === 'us') return 'USD';
  return DEFAULT_CURRENCY_CODE;
}

const fetchCountryFromIP = async (): Promise<string | null> => {
  try {
    const functions = getFunctions(firebaseApp);
    const getGeoLocationCallable = httpsCallable(functions, 'getGeoLocationFromIP');
    const result = await getGeoLocationCallable() as HttpsCallableResult<{ countryCode?: string | null; error?: string }>;
    if (result.data.error) return null;
    return result.data.countryCode ? result.data.countryCode.toUpperCase() : null;
  } catch (error) {
    return null;
  }
};

const getInitialGuestValues = (): { country: CurrencyCode, symbol: string } => {
  let country: CurrencyCode = DEFAULT_CURRENCY_CODE;
  let symbol: string = '$';
  if (typeof window !== 'undefined' && navigator.language) {
    const localeCountryCode = navigator.language.split('-')[1]?.toUpperCase();
    if (localeCountryCode) country = getCurrencyFromCountryCode(localeCountryCode);
  }
  const pricesForCountry = TIER_DETAILS[PRICING_TIERS.FREE].prices[country];
  const pricesForDefaultCountry = TIER_DETAILS[PRICING_TIERS.FREE].prices[DEFAULT_CURRENCY_CODE];
  symbol = (pricesForCountry?.currencySymbol) || (pricesForDefaultCountry?.currencySymbol) || '$';
  return { country, symbol };
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [optimisticUser, setOptimisticUser] = useState<OptimisticUserData | null>(null);
  const [currentTierState, setCurrentTierState] = useState<UserTier>(PRICING_TIERS.FREE);
  const [tierValidUntilState, setTierValidUntilState] = useState<Timestamp | null>(null);
  const [monthlyRepliesUsedState, setMonthlyRepliesUsedState] = useState<number>(0);
  const [monthlyImageUploadsUsedState, setMonthlyImageUploadsUsedState] = useState<number>(0);
  const [monthlyHintsHelpsUsedState, setMonthlyHintsHelpsUsedState] = useState<number>(0);
  const [isPremiumState, setIsPremiumState] = useState<boolean>(false); 
  const [premiumValidUntilState, setPremiumValidUntilState] = useState<Timestamp | null>(null); 
  const [premiumSourceState, setPremiumSourceState] = useState<'coupon' | 'razorpay' | 'razorpay_addon' | null>(null);
  const [isAdminState, setIsAdminState] = useState<boolean>(false);
  const [freeQuestionsUsedState, setFreeQuestionsUsedState] = useState<number>(0);
  const [referralCodeState, setReferralCodeState] = useState<string | null>(null);
  const [userCountryState, setUserCountryState] = useState<CurrencyCode>(() => getInitialGuestValues().country);
  const [currencySymbolState, setCurrencySymbolState] = useState<string>(() => getInitialGuestValues().symbol);
  const [lastFullFeedbackAddonPurchaseAtState, setLastFullFeedbackAddonPurchaseAtState] = useState<Timestamp | null>(null);
  const [dayPassActiveUntilState, setDayPassActiveUntilState] = useState<Timestamp | null>(null);
  const [lastDayPassPurchaseAtState, setLastDayPassPurchaseAtState] = useState<Timestamp | null>(null);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isGsiScriptLoaded, setIsGsiScriptLoaded] = useState(false);
  const [showIntegrationInProgressModal, setShowIntegrationInProgressModal] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const userProfileDataRef = useRef<UserProfileData | null>(null);
  const initialAuthCheckCompleteRef = useRef(false); // Ref for managing initial auth check completion
  const { razorpayIntegrationEnabled, loadingConfig: loadingRemoteConfig, razorpayMode } = useRemoteConfig(); 

  const deriveCurrencySymbol = useCallback((currencyCode: CurrencyCode): string => {
    return TIER_DETAILS[PRICING_TIERS.FREE]?.prices[currencyCode]?.currencySymbol ||
           TIER_DETAILS[PRICING_TIERS.FREE]?.prices[DEFAULT_CURRENCY_CODE]?.currencySymbol || '$';
  }, []);

  useEffect(() => {
    try {
      const storedOptimisticData = localStorage.getItem(OPTIMISTIC_USER_STORAGE_KEY);
      if (storedOptimisticData) setOptimisticUser(JSON.parse(storedOptimisticData));
    } catch (error) {}
  }, []);

  const updateOptimisticUserData = (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      const optimisticData: OptimisticUserData = {
        displayName: firebaseUser.displayName, photoURL: firebaseUser.photoURL, email: firebaseUser.email,
      };
      localStorage.setItem(OPTIMISTIC_USER_STORAGE_KEY, JSON.stringify(optimisticData));
      setOptimisticUser(optimisticData);
    } else {
      localStorage.removeItem(OPTIMISTIC_USER_STORAGE_KEY);
      setOptimisticUser(null);
    }
  };

  const _getInitialUserCountryGuess = useCallback(async (): Promise<CurrencyCode> => {
    const ipCountry = await fetchCountryFromIP();
    if (ipCountry) return getCurrencyFromCountryCode(ipCountry);
    if (typeof window !== 'undefined' && navigator.language) {
      const localeCountryCode = navigator.language.split('-')[1]?.toUpperCase();
      if (localeCountryCode) return getCurrencyFromCountryCode(localeCountryCode);
    }
    return DEFAULT_CURRENCY_CODE;
  }, []);

  const fetchUserProfile = useCallback(async (firebaseUser: User | null) => {
    setProfileLoading(true);
    if (firebaseUser && firebaseUser.uid && importedDbInstance) { // Added check for importedDbInstance
      try {
        const userDocRef = doc(importedDbInstance, 'users', firebaseUser.uid);
        let docSnap = await getDoc(userDocRef);
        let referredByCode: string | null = searchParams ? searchParams.get('ref') : null;
        
        const defaultProfileValuesBase: Omit<UserProfileData, 'createdAt' | 'lastLoginAt' | 'referralCode' | 'referredBy' | 'userCountry' | 'lastFullFeedbackAddonPurchaseAt' | 'dayPassActiveUntil' | 'lastDayPassPurchaseAt'> = {
            email: firebaseUser.email, displayName: firebaseUser.displayName, currentTier: PRICING_TIERS.FREE, tierValidUntil: null,
            monthlyRepliesUsed: 0, monthlyImageUploadsUsed: 0, monthlyHintsHelpsUsed: 0, lastMonthlyReset: Timestamp.now(),
            isPremium: false, premiumValidUntil: null, premiumSource: null, freeQuestionsUsed: 0, isAdmin: false,
        };
        let userProfileToUpdate: UserProfileData;
        let profileNeedsServerUpdate = false;
        let determinedUserCurrency: CurrencyCode;

        if (docSnap.exists()) {
            const dataFromDb = docSnap.data() as Partial<UserProfileData>;
            userProfileToUpdate = { ...defaultProfileValuesBase, ...dataFromDb } as UserProfileData;

            if (!userProfileToUpdate.referralCode) {
                userProfileToUpdate.referralCode = uuidv4().substring(0, 8);
                profileNeedsServerUpdate = true;
            }
            if (referredByCode && !userProfileToUpdate.referredBy) {
                userProfileToUpdate.referredBy = referredByCode;
                userProfileToUpdate.monthlyRepliesUsed = (userProfileToUpdate.monthlyRepliesUsed || 0) - REFEREE_SIGNUP_BONUS.replies;
                userProfileToUpdate.monthlyImageUploadsUsed = (userProfileToUpdate.monthlyImageUploadsUsed || 0) - REFEREE_SIGNUP_BONUS.images;
                userProfileToUpdate.monthlyHintsHelpsUsed = (userProfileToUpdate.monthlyHintsHelpsUsed || 0) - REFEREE_SIGNUP_BONUS.hintsHelps;
                toast({ title: "Welcome!", description: `You've received a signup bonus!`, duration: 3000});
                profileNeedsServerUpdate = true;
            }
            determinedUserCurrency = dataFromDb.userCountry || await _getInitialUserCountryGuess();
            if (!dataFromDb.userCountry) {
                userProfileToUpdate.userCountry = determinedUserCurrency;
                profileNeedsServerUpdate = true;
            }
            
            const now = new Date();
            const lastReset = userProfileToUpdate.lastMonthlyReset?.toDate();
            if (lastReset && (lastReset.getFullYear() < now.getFullYear() || lastReset.getMonth() < now.getMonth()) && userProfileToUpdate.currentTier !== PRICING_TIERS.PAY_PER_USE) {
                userProfileToUpdate.monthlyRepliesUsed = (referredByCode && !dataFromDb.referredBy && profileNeedsServerUpdate && userProfileToUpdate.createdAt?.toDate().getMonth() === now.getMonth()) ? -REFEREE_SIGNUP_BONUS.replies : 0;
                userProfileToUpdate.monthlyImageUploadsUsed = (referredByCode && !dataFromDb.referredBy && profileNeedsServerUpdate && userProfileToUpdate.createdAt?.toDate().getMonth() === now.getMonth()) ? -REFEREE_SIGNUP_BONUS.images : 0;
                userProfileToUpdate.monthlyHintsHelpsUsed = (referredByCode && !dataFromDb.referredBy && profileNeedsServerUpdate && userProfileToUpdate.createdAt?.toDate().getMonth() === now.getMonth()) ? -REFEREE_SIGNUP_BONUS.hintsHelps : 0;
                userProfileToUpdate.lastMonthlyReset = Timestamp.now();
                profileNeedsServerUpdate = true;
            }

            if (userProfileToUpdate.currentTier === PRICING_TIERS.PAY_PER_USE && userProfileToUpdate.tierValidUntil && userProfileToUpdate.tierValidUntil.toDate() < now) {
                userProfileToUpdate.currentTier = PRICING_TIERS.FREE;
                userProfileToUpdate.tierValidUntil = null;
                userProfileToUpdate.monthlyRepliesUsed = 0;
                userProfileToUpdate.monthlyImageUploadsUsed = 0;
                userProfileToUpdate.monthlyHintsHelpsUsed = 0;
                userProfileToUpdate.dayPassActiveUntil = null; 
                profileNeedsServerUpdate = true;
            }

            if (userProfileToUpdate.dayPassActiveUntil && userProfileToUpdate.dayPassActiveUntil.toDate() < now) {
                userProfileToUpdate.dayPassActiveUntil = null;
                profileNeedsServerUpdate = true;
            }

        } else { 
            determinedUserCurrency = await _getInitialUserCountryGuess();
            userProfileToUpdate = {
                ...defaultProfileValuesBase, email: firebaseUser.email, displayName: firebaseUser.displayName, createdAt: serverTimestamp(),
                referralCode: uuidv4().substring(0, 8), referredBy: referredByCode, userCountry: determinedUserCurrency,
                monthlyRepliesUsed: referredByCode ? -REFEREE_SIGNUP_BONUS.replies : 0,
                monthlyImageUploadsUsed: referredByCode ? -REFEREE_SIGNUP_BONUS.images : 0,
                monthlyHintsHelpsUsed: referredByCode ? -REFEREE_SIGNUP_BONUS.hintsHelps : 0,
                lastFullFeedbackAddonPurchaseAt: null,
                dayPassActiveUntil: null,
                lastDayPassPurchaseAt: null,
            } as UserProfileData;
            if (referredByCode) toast({ title: "Welcome!", description: `You've received a signup bonus!`, duration: 3000});
            profileNeedsServerUpdate = true;
        }
        userProfileToUpdate.lastLoginAt = serverTimestamp();

        setCurrentTierState(userProfileToUpdate.currentTier || PRICING_TIERS.FREE);
        setTierValidUntilState(userProfileToUpdate.tierValidUntil || null);
        setMonthlyRepliesUsedState(userProfileToUpdate.monthlyRepliesUsed || 0);
        setMonthlyImageUploadsUsedState(userProfileToUpdate.monthlyImageUploadsUsed || 0);
        setMonthlyHintsHelpsUsedState(userProfileToUpdate.monthlyHintsHelpsUsed || 0);
        
        const derivedIsPremium = (userProfileToUpdate.currentTier !== PRICING_TIERS.FREE && userProfileToUpdate.currentTier !== PRICING_TIERS.PAY_PER_USE) && 
                                 userProfileToUpdate.tierValidUntil != null && userProfileToUpdate.tierValidUntil.toDate() >= new Date();
        setIsPremiumState(derivedIsPremium);
        setPremiumValidUntilState(derivedIsPremium ? userProfileToUpdate.tierValidUntil : null);
        
        setPremiumSourceState(userProfileToUpdate.premiumSource || null);
        setIsAdminState(userProfileToUpdate.isAdmin || false);
        setFreeQuestionsUsedState(userProfileToUpdate.freeQuestionsUsed || 0);
        setReferralCodeState(userProfileToUpdate.referralCode);
        setUserCountryState(determinedUserCurrency);
        setCurrencySymbolState(deriveCurrencySymbol(determinedUserCurrency));
        setLastFullFeedbackAddonPurchaseAtState(userProfileToUpdate.lastFullFeedbackAddonPurchaseAt || null);
        setDayPassActiveUntilState(userProfileToUpdate.dayPassActiveUntil || null);
        setLastDayPassPurchaseAtState(userProfileToUpdate.lastDayPassPurchaseAt || null);

        userProfileDataRef.current = userProfileToUpdate; 
        updateOptimisticUserData(firebaseUser);

        if (profileNeedsServerUpdate) {
            await setDoc(userDocRef, userProfileToUpdate, { merge: true });
        } else if (docSnap.exists()) { 
            await updateDoc(userDocRef, { lastLoginAt: serverTimestamp() });
        }
        
      } catch (error) {
        console.error("Error fetching/updating user profile:", error);
        toast({ title: "Profile Error", description: "Could not load user profile.", variant: "destructive" });
        const guestDefaults = getInitialGuestValues(); setUserCountryState(guestDefaults.country); setCurrencySymbolState(guestDefaults.symbol);
        setCurrentTierState(PRICING_TIERS.FREE); setTierValidUntilState(null);
        setLastFullFeedbackAddonPurchaseAtState(null); setDayPassActiveUntilState(null); setLastDayPassPurchaseAtState(null);
      }
    } else { 
      userProfileDataRef.current = null; const guestDefaults = getInitialGuestValues();
      setUserCountryState(guestDefaults.country); setCurrencySymbolState(guestDefaults.symbol);
      setCurrentTierState(PRICING_TIERS.FREE); setTierValidUntilState(null);
      setMonthlyRepliesUsedState(0); setMonthlyImageUploadsUsedState(0); setMonthlyHintsHelpsUsedState(0);
      setIsPremiumState(false); setPremiumValidUntilState(null); setPremiumSourceState(null);
      setIsAdminState(false); setFreeQuestionsUsedState(0); setReferralCodeState(null);
      setLastFullFeedbackAddonPurchaseAtState(null); setDayPassActiveUntilState(null); setLastDayPassPurchaseAtState(null);
      updateOptimisticUserData(null);
    }
    setProfileLoading(false);
  }, [toast, searchParams, _getInitialUserCountryGuess, deriveCurrencySymbol]);

  const fetchAndSetDisplayCountry = useCallback(async () => {
    if (user && userProfileDataRef.current?.userCountry) return;
    const guessedCurrency = await _getInitialUserCountryGuess();
    setUserCountryState(guessedCurrency);
    setCurrencySymbolState(deriveCurrencySymbol(guessedCurrency));
  }, [user, _getInitialUserCountryGuess, deriveCurrencySymbol]);

  useEffect(() => {
    const authContextPrefix = `AuthContext.useEffect[onIdTokenChanged]`;
    let unsubscribe: (() => void) | undefined;

    if (!auth) { // Check if the imported auth object from @/lib/firebase is null
      console.warn(`${authContextPrefix} - Firebase Auth service is NOT available (imported as null). User authentication will not function.`);
      setUser(null);
      updateOptimisticUserData(null);
      // Reset all states to their "logged out" or default values
      const guestDefaults = getInitialGuestValues();
      setUserCountryState(guestDefaults.country);
      setCurrencySymbolState(guestDefaults.symbol);
      setCurrentTierState(PRICING_TIERS.FREE);
      setTierValidUntilState(null);
      setMonthlyRepliesUsedState(0);
      setMonthlyImageUploadsUsedState(0);
      setMonthlyHintsHelpsUsedState(0);
      setIsPremiumState(false);
      setPremiumValidUntilState(null);
      setPremiumSourceState(null);
      setIsAdminState(false);
      setFreeQuestionsUsedState(0);
      setReferralCodeState(null);
      setLastFullFeedbackAddonPurchaseAtState(null);
      setDayPassActiveUntilState(null);
      setLastDayPassPurchaseAtState(null);
      
      if (!initialAuthCheckCompleteRef.current) {
        setInitialAuthCheckComplete(true);
        initialAuthCheckCompleteRef.current = true;
      }
      setProfileLoading(false);
      return; // Early exit if Firebase Auth module itself is not loaded
    }

    // If auth is not null, it's considered a valid Auth instance from Firebase.
    unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser as User | null);
      await fetchUserProfile(firebaseUser as User | null);
      
      if (!initialAuthCheckCompleteRef.current) {
        setInitialAuthCheckComplete(true);
        initialAuthCheckCompleteRef.current = true;
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [fetchUserProfile]); // `auth` is from module scope. `fetchUserProfile` is memoized.

  useEffect(() => {
    const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || auth?.app?.options?.clientId;
    if (!googleClientId) return;
    if (typeof window !== 'undefined' && window.google && window.google.accounts && window.google.accounts.id) setIsGsiScriptLoaded(true);
    else {
      const intervalId = setInterval(() => {
        if (typeof window !== 'undefined' && window.google && window.google.accounts && window.google.accounts.id) {
          setIsGsiScriptLoaded(true); clearInterval(intervalId);
        }
      }, 200);
      return () => clearInterval(intervalId);
    }
    if (isGsiScriptLoaded && initialAuthCheckComplete && !user && !profileLoading && auth) { // Added auth check here
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response: any) => {
            if (!auth) return; // Guard against null auth
            try {
              const idToken = response.credential;
              const firebaseCredential = GoogleAuthProvider.credential(idToken);
              await signInWithCredential(auth, firebaseCredential);
              toast({ title: "Signed In", description: "Successfully signed in."});
            } catch (error: any) {
              toast({ variant: "destructive", title: "One Tap Sign In Error", description: error.message || "Failed to sign in."});
            }
          },
        });
        window.google.accounts.id.prompt();
      } catch (e) {}
    }
  }, [isGsiScriptLoaded, initialAuthCheckComplete, user, profileLoading, toast, auth]);

  const signInWithGoogle = useCallback(async () => {
    if (!auth) {
      toast({ variant: "destructive", title: "Auth Error", description: "Auth service not ready."}); return;
    }
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      toast({ title: "Signed In", description: "Successfully signed in."});
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') toast({ title: "Sign In Cancelled", description: "Popup closed.", variant: "default"});
      else toast({ variant: "destructive", title: "Sign In Error", description: error.message || "Failed to sign in."});
    }
  }, [toast, auth]);

  const logout = useCallback(async () => {
    if (!auth) {
      toast({ variant: "destructive", title: "Auth Error", description: "Auth service not ready."});
      if (!initialAuthCheckCompleteRef.current) {
        setInitialAuthCheckComplete(true);
        initialAuthCheckCompleteRef.current = true;
      }
      setProfileLoading(false); return;
    }
    if (!initialAuthCheckCompleteRef.current) setInitialAuthCheckComplete(true); // Mark as complete for loading state
    initialAuthCheckCompleteRef.current = true; // Ensure this is set
    setProfileLoading(true);
    try {
      await firebaseSignOut(auth);
      if (window.google && window.google.accounts && window.google.accounts.id) window.google.accounts.id.disableAutoSelect();
      updateOptimisticUserData(null); 
      // Reset all user-specific states to default/logged-out values
      const guestDefaults = getInitialGuestValues();
      setUserCountryState(guestDefaults.country);
      setCurrencySymbolState(guestDefaults.symbol);
      setCurrentTierState(PRICING_TIERS.FREE);
      setTierValidUntilState(null);
      setMonthlyRepliesUsedState(0);
      setMonthlyImageUploadsUsedState(0);
      setMonthlyHintsHelpsUsedState(0);
      setIsPremiumState(false);
      setPremiumValidUntilState(null);
      setPremiumSourceState(null);
      setIsAdminState(false);
      setFreeQuestionsUsedState(0);
      setReferralCodeState(null);
      setLastFullFeedbackAddonPurchaseAtState(null);
      setDayPassActiveUntilState(null);
      setLastDayPassPurchaseAtState(null);

      setProfileLoading(false);
      toast({ title: "Signed Out", description: "Successfully signed out."});
      router.push('/');
    } catch (error: any) {
      toast({ variant: "destructive", title: "Sign Out Error", description: error.message || "Failed to sign out."});
      setProfileLoading(false);
    }
  }, [router, toast, auth]);

  const refreshSubscriptionStatus = useCallback(async () => { await fetchUserProfile(user); }, [user, fetchUserProfile]);

  const isEffectivelyPremiumFn = useMemo(() => {
    if (isAdminState) return true;
    // Active Standard or Premium tier means effectively premium (ad-free, full features)
    if ((currentTierState === PRICING_TIERS.STANDARD || currentTierState === PRICING_TIERS.PREMIUM) &&
        tierValidUntilState && tierValidUntilState.toDate() >= new Date()) {
        return true;
    }
    return false;
  }, [currentTierState, tierValidUntilState, isAdminState]);

  const getQuotaForFn = useCallback((metric: 'replies' | 'images' | 'hintsHelps' | 'feedback'): number | 'unlimited' => {
    const now = new Date();
    let baseQuota: number | 'unlimited' = 0;
    let baseTierConfig = TIER_DETAILS[currentTierState as UserTier] || TIER_DETAILS[PRICING_TIERS.FREE];

    // Check if user is on a Day Pass that is currently active
    const isOnActiveDayPassTier = currentTierState === PRICING_TIERS.PAY_PER_USE && tierValidUntilState && tierValidUntilState.toDate() >= now;
    // Check if user has an active Day Pass top-up on their monthly plan
    const hasActiveDayPassTopUp = dayPassActiveUntilState && dayPassActiveUntilState.toDate() >= now;

    if (isOnActiveDayPassTier) {
        baseTierConfig = TIER_DETAILS[PRICING_TIERS.PAY_PER_USE]; 
        switch (metric) {
            case 'replies': baseQuota = baseTierConfig.dailyRepliesLimit ?? 0; break;
            case 'images': baseQuota = baseTierConfig.dailyImagesLimit ?? 0; break;
            case 'hintsHelps': baseQuota = baseTierConfig.dailyHintsHelpsLimit ?? 0; break;
            case 'feedback': baseQuota = baseTierConfig.feedbackType === 'full' ? 'unlimited' : 0; break;
        }
    } else { // Free, Basic, Standard, Premium
        if (metric === 'feedback') {
            baseQuota = baseTierConfig.feedbackType === 'full' ? 'unlimited' : 0;
        } else {
            const monthlyQuotaKey = metric === 'replies' ? 'repliesPerMonth' : metric === 'images' ? 'imagesPerMonth' : 'hintsHelpsPerMonth';
            baseQuota = baseTierConfig[monthlyQuotaKey] ?? TIER_DETAILS[PRICING_TIERS.FREE][monthlyQuotaKey] ?? 0;
        }
    }
    
    // If user has an active Day Pass top-up, add its daily bonus to the base quota for that day
    // This does NOT apply if they are already on the PAY_PER_USE tier itself.
    if (hasActiveDayPassTopUp && currentTierState !== PRICING_TIERS.PAY_PER_USE && baseQuota !== 'unlimited') {
        const dayPassBonusConfig = TIER_DETAILS[PRICING_TIERS.PAY_PER_USE];
        let bonus = 0;
        switch (metric) {
            case 'replies': bonus = dayPassBonusConfig.dailyRepliesLimit ?? 0; break;
            case 'images': bonus = dayPassBonusConfig.dailyImagesLimit ?? 0; break;
            case 'hintsHelps': bonus = dayPassBonusConfig.dailyHintsHelpsLimit ?? 0; break;
        }
        baseQuota = (baseQuota as number) + bonus; // Add bonus to monthly
    }


    if (metric === 'feedback') {
        if (baseQuota === 'unlimited') return 'unlimited'; // If tier itself gives full feedback
        if (lastFullFeedbackAddonPurchaseAtState) {
            const addonPurchaseTime = lastFullFeedbackAddonPurchaseAtState.toDate();
            if (now.getTime() - addonPurchaseTime.getTime() < 60 * 60 * 1000) { // e.g., 1 hour window for the addon
                return 'unlimited'; 
            }
        }
    }
    return baseQuota;
  }, [currentTierState, tierValidUntilState, dayPassActiveUntilState, lastFullFeedbackAddonPurchaseAtState]);


  const incrementUsage = useCallback(async (metric: 'replies' | 'images' | 'hintsHelps' | 'feedback', amount: number = 1) => {
    if (!user || !importedDbInstance) return;
    
    if (metric === 'feedback' && getQuotaForFn('feedback') === 'unlimited') return;

    const userDocRef = doc(importedDbInstance, 'users', user.uid);
    let fieldToUpdate: keyof UserProfileData | null = null;
    let currentStateVal = 0;

    switch (metric) {
        case 'replies': 
            if (getQuotaForFn('replies') === 'unlimited') return; 
            fieldToUpdate = 'monthlyRepliesUsed'; 
            currentStateVal = monthlyRepliesUsedState; 
            setMonthlyRepliesUsedState(prev => prev + amount); 
            break;
        case 'images': 
            if (getQuotaForFn('images') === 'unlimited') return; 
            fieldToUpdate = 'monthlyImageUploadsUsed'; 
            currentStateVal = monthlyImageUploadsUsedState; 
            setMonthlyImageUploadsUsedState(prev => prev + amount); 
            break;
        case 'hintsHelps': 
            if (getQuotaForFn('hintsHelps') === 'unlimited') return; 
            fieldToUpdate = 'monthlyHintsHelpsUsed'; 
            currentStateVal = monthlyHintsHelpsUsedState; 
            setMonthlyHintsHelpsUsedState(prev => prev + amount); 
            break;
        default: return;
    }
    try { if (fieldToUpdate) await setDoc(userDocRef, { [fieldToUpdate]: currentStateVal + amount }, { merge: true }); }
    catch (error) { 
        switch (metric) {
            case 'replies': setMonthlyRepliesUsedState(currentStateVal); break;
            case 'images': setMonthlyImageUploadsUsedState(currentStateVal); break;
            case 'hintsHelps': setMonthlyHintsHelpsUsedState(currentStateVal); break;
        }
        toast({ title: "Usage Update Error", description: `Could not update ${metric} count.`, variant: "destructive" });
    }
  }, [user, monthlyRepliesUsedState, monthlyImageUploadsUsedState, monthlyHintsHelpsUsedState, toast, getQuotaForFn]);

  const processRazorpayPayment = useCallback(async (action: 'subscribeToTier' | 'purchaseAddon' | 'purchasePayPerUseSession', details: Record<string, any>) => {
    if (!user) { toast({ title: "Login Required", variant: "destructive" }); return; }

    let clientRazorpayKeyId: string | undefined;
    if (razorpayMode === 'live') {
        clientRazorpayKeyId = process.env.NEXT_PUBLIC_RAZORPAY_LIVE_KEY_ID;
    } else { 
        clientRazorpayKeyId = process.env.NEXT_PUBLIC_RAZORPAY_TEST_KEY_ID;
    }
     if (!clientRazorpayKeyId) {
        toast({ title: "Config Error", description: `Razorpay ${razorpayMode.toUpperCase()} Key ID not set.`, variant: "destructive", duration: 3000 });
        return;
    }


    if (!window.Razorpay) { toast({ title: "SDK Error", description: "Razorpay SDK not loaded.", variant: "destructive" }); return; }

    let amount = 0; let determinedCurrency: CurrencyCode = userCountryState;
    let description = "IntervuAI Purchase"; let tierNameForFunctionCall: string | null = null;

    if (action === 'subscribeToTier' && details.tierName) {
      const tierKey = Object.keys(TIER_DETAILS).find(k => TIER_DETAILS[k as keyof typeof TIER_DETAILS].name === details.tierName) as UserTier | undefined;
      if (tierKey && TIER_DETAILS[tierKey]) {
        tierNameForFunctionCall = TIER_DETAILS[tierKey].name; 
        const priceDetail = TIER_DETAILS[tierKey].prices[determinedCurrency] || TIER_DETAILS[tierKey].prices['USD'];
        amount = priceDetail?.monthly || 0; description = `Subscription to ${TIER_DETAILS[tierKey].name}`;
      }
    } else if (action === 'purchasePayPerUseSession') { 
        tierNameForFunctionCall = TIER_DETAILS.payPerUseOption.name; 
        const priceDetail = TIER_DETAILS.payPerUseOption.prices[determinedCurrency] || TIER_DETAILS.payPerUseOption.prices['USD'];
        amount = priceDetail?.daily || 0; description = "IntervuAI Day Pass";
    } else if (action === 'purchaseAddon' && details.addon && ADDON_PRICES[details.addon as keyof typeof ADDON_PRICES]) {
        const addonKey = details.addon as keyof typeof ADDON_PRICES;
        const addonDetails = ADDON_PRICES[addonKey]?.[determinedCurrency] || ADDON_PRICES[addonKey]?.['USD'];
        amount = addonDetails?.price || 0;
        description = `IntervuAI Add-on: ${details.addon.replace(/_/g, ' ').replace(/\bEXTRA\b/i, '').trim()}`;
        tierNameForFunctionCall = `ADDON_${details.addon}`;
    }
    if (amount <= 0) { toast({ title: "Invalid Amount", variant: "destructive" }); return; }
    toast({ title: "Creating Order...", duration: 3000 });
    let orderId: string | null = null;
    try {
      const functions = getFunctions(firebaseApp);
      const createOrderCallable = httpsCallable(functions, 'createRazorpayOrder');
      const orderResult = await createOrderCallable({ amount: Math.round(amount * 100), currency: determinedCurrency, tierName: tierNameForFunctionCall }) as { data: {success: boolean; orderId?: string; message?: string}};
      if (orderResult.data.success && orderResult.data.orderId) orderId = orderResult.data.orderId;
      else throw new Error(orderResult.data.message || "Failed to create order.");
    } catch (error: any) {
      toast({ title: "Order Creation Failed", description: `${error.message || "Could not create order."} (Ensure domain is whitelisted for live keys in Razorpay dashboard.)`, variant: "destructive", duration: 7000 }); return;
    }
    if (!orderId) { toast({ title: "Order ID Missing", variant: "destructive" }); return; }

    const options = {
      key: clientRazorpayKeyId, amount: Math.round(amount * 100), currency: determinedCurrency,
      name: "IntervuAI", description: description, order_id: orderId,
      handler: async (response: RazorpayPaymentResponse) => {
        toast({ title: "Payment Processing...", duration: 3000 });
        try {
          const functions = getFunctions(firebaseApp);
          const verifyPaymentCallable = httpsCallable(functions, 'verifyRazorpayPayment');
          const verificationResult = await verifyPaymentCallable({ ...response, tierName: tierNameForFunctionCall }) as {data: {success: boolean; message?: string}};
          if (verificationResult.data.success) {
            toast({ title: "Payment Verified!", description: verificationResult.data.message || "Subscription updated." });
            await refreshSubscriptionStatus(); 
          } else throw new Error(verificationResult.data.message || "Verification failed.");
        } catch (verificationError: any) {
            toast({ title: "Verification Failed", description: verificationError.message || "Contact support.", variant: "destructive", duration: 5000 });
        }
      },
      modal: { ondismiss: function() { toast({ title: "Payment Cancelled", variant: "default" }); } }
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response: any){
      toast({ title: "Payment Failed", description: `${response.error.description || 'Error occurred.'} (Code: ${response.error.code})`, variant: "destructive", duration: 5000 });
    });
    rzp.open();
  }, [user, toast, userCountryState, razorpayMode, refreshSubscriptionStatus]);

  const triggerUpsell = useCallback(async (action: 'subscribeToTier' | 'purchaseAddon' | 'purchasePayPerUseSession', details?: Record<string, any>) => {
    if (!user) { toast({ title: "Login Required", variant: "destructive" }); router.push('/login'); return; }
    if (!razorpayIntegrationEnabled && (action === 'subscribeToTier' || action === 'purchasePayPerUseSession' || action === 'purchaseAddon')) {
      setShowIntegrationInProgressModal(true); return;
    }
    
    let effectiveDetails = details || {};
    if (action === 'purchasePayPerUseSession' && !effectiveDetails.tierName) {
      effectiveDetails = { ...effectiveDetails, tierName: TIER_DETAILS.payPerUseOption.name };
    }
    
    await processRazorpayPayment(action, effectiveDetails);
  }, [toast, user, router, razorpayIntegrationEnabled, processRazorpayPayment]);

  const updateUserPreferredCurrency = useCallback(async (newCurrency: CurrencyCode) => {
    if (!user || !importedDbInstance) { toast({ title: "Not Logged In", variant: "destructive" }); return; }
    try {
      const userDocRef = doc(importedDbInstance, 'users', user.uid);
      await updateDoc(userDocRef, { userCountry: newCurrency });
      setUserCountryState(newCurrency); setCurrencySymbolState(deriveCurrencySymbol(newCurrency));
      if (userProfileDataRef.current) userProfileDataRef.current.userCountry = newCurrency;
      toast({ title: "Currency Updated", description: `Preferred currency: ${newCurrency}.` });
    } catch (error) {
      toast({ title: "Update Failed", description: "Could not update currency preference.", variant: "destructive" });
    }
  }, [user, toast, deriveCurrencySymbol]);

  const overallLoading = !initialAuthCheckComplete || profileLoading || loadingRemoteConfig;

  // Effect to set/remove the 'premium_user' cookie
  useEffect(() => {
    if (typeof window !== 'undefined') { // Ensure this runs only on the client
      if (isEffectivelyPremiumFn) {
        document.cookie = "premium_user=true; path=/; max-age=31536000; SameSite=Lax"; // 1 year, SameSite=Lax for security
      } else {
        // Explicitly set to false or remove. Setting to false is often better for checks.
        document.cookie = "premium_user=false; path=/; max-age=31536000; SameSite=Lax"; // 1 year
        // To remove instead: document.cookie = "premium_user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax";
      }
    }
  }, [isEffectivelyPremiumFn, overallLoading]); // Rerun when premium status or overall loading status changes

  const value = useMemo(() => ({
    user, optimisticUser, loading: overallLoading, currentTier: currentTierState, tierValidUntil: tierValidUntilState,
    isEffectivelyPremium: isEffectivelyPremiumFn, monthlyRepliesUsed: monthlyRepliesUsedState,
    monthlyImageUploadsUsed: monthlyImageUploadsUsedState, monthlyHintsHelpsUsed: monthlyHintsHelpsUsedState,
    getQuotaFor: getQuotaForFn, isPremium: isPremiumState, premiumValidUntil: premiumValidUntilState,
    premiumSource: premiumSourceState, isAdmin: isAdminState, freeQuestionsUsed: freeQuestionsUsedState,
    referralCode: referralCodeState, userCountry: userCountryState, currencySymbol: currencySymbolState,
    lastFullFeedbackAddonPurchaseAt: lastFullFeedbackAddonPurchaseAtState,
    dayPassActiveUntil: dayPassActiveUntilState, lastDayPassPurchaseAt: lastDayPassPurchaseAtState,
    signInWithGoogle, logout, refreshSubscriptionStatus, incrementUsage, triggerUpsell, fetchAndSetDisplayCountry,
    updateUserPreferredCurrency,
  }), [
    user, optimisticUser, overallLoading, currentTierState, tierValidUntilState, isEffectivelyPremiumFn,
    monthlyRepliesUsedState, monthlyImageUploadsUsedState, monthlyHintsHelpsUsedState, getQuotaForFn,
    isPremiumState, premiumValidUntilState, premiumSourceState, isAdminState, freeQuestionsUsedState,
    referralCodeState, userCountryState, currencySymbolState, lastFullFeedbackAddonPurchaseAtState,
    dayPassActiveUntilState, lastDayPassPurchaseAtState,
    signInWithGoogle, logout, refreshSubscriptionStatus, incrementUsage, triggerUpsell,
    fetchAndSetDisplayCountry, updateUserPreferredCurrency
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <IntegrationInProgressModal isOpen={showIntegrationInProgressModal} onOpenChange={setShowIntegrationInProgressModal} />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
